import type { ChatStatus, SilentReason } from '../types';

interface StatusBadgeProps {
  status: ChatStatus;
  silentReason?: SilentReason | null;
}

const styles: Record<ChatStatus, string> = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  silent: 'bg-amber-50 text-amber-700 border-amber-200',
  disabled: 'bg-gray-50 text-gray-700 border-gray-200',
  kicked: 'bg-red-50 text-red-700 border-red-200'
};

const silentReasonStyles: Record<SilentReason, string> = {
  'no-posts':      'bg-amber-50 text-amber-700 border-amber-200',
  'render-failed': 'bg-orange-50 text-orange-700 border-orange-200',
  'no-credits':    'bg-red-50 text-red-700 border-red-200',
  'never-fired':   'bg-gray-50 text-gray-700 border-gray-200',
};

const silentReasonLabels: Record<SilentReason, string> = {
  'no-posts':      'no posts',
  'render-failed': 'render failed',
  'no-credits':    'no credits',
  'never-fired':   'never fired',
};

const silentReasonTitles: Record<SilentReason, string> = {
  'no-posts':      'Bot fires on schedule but the X API returns 0 posts. Broaden the filter or accept low-volume cashtag.',
  'render-failed': 'Fetch found posts but the leaderboard renderer returned an error (e.g. all posts filtered out downstream). Investigate the X-Performance-Report state machine.',
  'no-credits':    'Monthly quota or credit balance exhausted.',
  'never-fired':   'Schedule is set but no fetch has been observed yet (heartbeat may be new, or schedule was just configured).',
};

export function StatusBadge({ status, silentReason }: StatusBadgeProps) {
  if (status === 'silent' && silentReason) {
    return (
      <span
        data-testid={`status-badge-silent-${silentReason}`}
        title={silentReasonTitles[silentReason]}
        className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${silentReasonStyles[silentReason]}`}
      >
        silent · {silentReasonLabels[silentReason]}
      </span>
    );
  }
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize ${styles[status] ?? ''}`}
    >
      {status}
    </span>
  );
}
